import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { IoCheckmark, IoClose, IoGrid } from 'react-icons/io5';
import './Pricing.css';

const FREE_FEATURES = [
  { type: 'check', text: <>News — <strong>Unlimited</strong> views</> },
  { type: 'check', text: <>Datasets — <strong>5</strong>, up to <strong>5 views</strong> each</> },
  { type: 'check', text: <>Documents — <strong>5</strong>, up to <strong>5 views</strong> each</> },
  { type: 'check', text: <>Infographics — <strong>1</strong>, up to <strong>5 views</strong></> },
  { type: 'cross', text: 'No downloads' },
  { type: 'cross', text: 'A short ad before each view' },
  { type: 'cross', text: 'No Sonaroid Workspace' },
];

const PREMIUM_FEATURES = [
  { type: 'check', text: <>News — <strong>Unlimited</strong> views</> },
  { type: 'check', text: <>Datasets — <strong>10</strong>, up to <strong>30 views</strong> each</> },
  { type: 'check', text: <>Documents — <strong>10</strong>, up to <strong>30 views</strong> each</> },
  { type: 'check', text: <>Infographics — <strong>5</strong>, up to <strong>30 views</strong> each</> },
  { type: 'check', text: <>Download <strong>2 datasets</strong> and <strong>3 documents</strong></> },
  { type: 'check', text: <><strong>No ads</strong>, anywhere</> },
];

const ENTERPRISE_FEATURES = [
  { type: 'check', text: <>News — <strong>Unlimited</strong> views</> },
  { type: 'check', text: <>Datasets — <strong>Unlimited</strong> views</> },
  { type: 'check', text: <>Documents — <strong>Unlimited</strong> views</> },
  { type: 'check', text: <>Infographics — <strong>Unlimited</strong> views</> },
  { type: 'check', text: <><strong>No ads</strong>, anywhere</> },
  {
    type: 'workspace',
    text: (
      <>
        <strong>Sonaroid Workspace</strong> — bespoke analytics inside your
        internal workflows
      </>
    ),
  },
];

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const normalizePlanName = (name) => String(name || '').trim().toLowerCase();

const getCurrencySymbol = (currencyCode) => {
  const code = String(currencyCode || '').toUpperCase();
  if (code === 'USD') return '$';
  if (code === 'NGN') return '₦';
  return code ? `${code} ` : '$';
};

const FeatureRow = ({ type, text, dark = false }) => (
  <div className={`pricing-feature-row${dark ? ' pricing-feature-row--dark' : ''}`}>
    {type === 'check' && (
      <span className="pricing-feature-icon pricing-feature-icon--check" aria-hidden="true">
        <IoCheckmark size={12} color="#036B26" />
      </span>
    )}
    {type === 'cross' && (
      <span className="pricing-feature-icon pricing-feature-icon--cross" aria-hidden="true">
        <IoClose size={12} color="#D42620" />
      </span>
    )}
    {type === 'workspace' && (
      <span className="pricing-feature-icon pricing-feature-icon--workspace" aria-hidden="true">
        <IoGrid size={12} color="#E65D25" />
      </span>
    )}
    <p>{text}</p>
  </div>
);

const emptyContactForm = {
  name: '',
  email: '',
  company_brief: '',
};

const Pricing = () => {
  const baseUrl = process.env.REACT_APP_BASE_URL;
  const [subscription, setSubscription] = useState([]);
  const [loading, setLoading] = useState(true);
  const [contactSalesLoading, setContactSalesLoading] = useState(false);
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const [contactForm, setContactForm] = useState(emptyContactForm);
  const [formError, setFormError] = useState('');
  const [toast, setToast] = useState(null);

  useEffect(() => {
    let active = true;
    setLoading(true);

    axios
      .get(`${baseUrl}subscription`)
      .then((res) => {
        if (!active) return;
        const plans = Array.isArray(res.data?.data) ? res.data.data : [];
        setSubscription(
          plans.filter((plan) => normalizePlanName(plan?.name) !== 'standard')
        );
      })
      .catch(() => {
        if (!active) return;
        setSubscription([]);
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [baseUrl]);

  useEffect(() => {
    if (!toast) return undefined;
    const timer = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(timer);
  }, [toast]);

  useEffect(() => {
    if (!isContactModalOpen) return undefined;

    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        setIsContactModalOpen(false);
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isContactModalOpen]);

  const freePlan = useMemo(
    () => subscription.find((plan) => normalizePlanName(plan?.name) === 'free'),
    [subscription]
  );
  const premiumPlan = useMemo(
    () => subscription.find((plan) => normalizePlanName(plan?.name) === 'premium'),
    [subscription]
  );

  const displayCurrencyCode = String(
    premiumPlan?.display_currency || freePlan?.display_currency || 'USD'
  ).toUpperCase();

  // const displayCurrencyLabel =
  //   displayCurrencyCode === 'NGN'
  //     ? 'Naira'
  //     : displayCurrencyCode === 'USD'
  //       ? 'USD'
  //       : displayCurrencyCode;

  // const detectedLocation =
  //   displayCurrencyCode === 'NGN' ? 'Nigeria' : 'your region';

  const formatPlanPrice = (plan, fallbackCurrency) => {
    if (!plan) {
      return {
        symbol: getCurrencySymbol(fallbackCurrency || 'USD'),
        amountLabel: '0',
      };
    }

    const currency =
      plan.display_currency || plan.billed_in || fallbackCurrency || 'USD';
    const symbol = getCurrencySymbol(currency);
    const amount =
      plan.display_amount != null
        ? Number(plan.display_amount)
        : Number(plan.amount_usd) || 0;

    return {
      symbol,
      amountLabel: amount.toLocaleString('en-US', {
        minimumFractionDigits: amount % 1 === 0 ? 0 : 2,
        maximumFractionDigits: 2,
      }),
    };
  };

  const freePrice = formatPlanPrice(freePlan, displayCurrencyCode);
  const premiumPrice = formatPlanPrice(premiumPlan, displayCurrencyCode);

  const openContactModal = () => {
    setFormError('');
    setContactForm(emptyContactForm);
    setIsContactModalOpen(true);
  };

  const closeContactModal = () => {
    if (contactSalesLoading) return;
    setIsContactModalOpen(false);
    setFormError('');
  };

  const handleContactFieldChange = (event) => {
    const { name, value } = event.target;
    setContactForm((prev) => ({ ...prev, [name]: value }));
    if (formError) setFormError('');
  };

  const handleContactSalesSubmit = async (event) => {
    event.preventDefault();
    if (contactSalesLoading) return;

    const name = contactForm.name.trim();
    const email = contactForm.email.trim();
    const companyBrief = contactForm.company_brief.trim();

    if (!name || !email) {
      setFormError('Name and email are required.');
      return;
    }

    if (!EMAIL_REGEX.test(email)) {
      setFormError('Please enter a valid email address.');
      return;
    }

    const payload = { name, email };
    if (companyBrief) {
      payload.company_brief = companyBrief;
    }

    setContactSalesLoading(true);
    try {
      const response = await axios.post(
        `${baseUrl}enterprise/contact-sales`,
        payload,
        {
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );
      setIsContactModalOpen(false);
      setContactForm(emptyContactForm);
      setToast({
        type: 'success',
        message:
          response?.data?.message ||
          'Thanks! Our sales team will contact you shortly.',
      });
    } catch (error) {
      setFormError(
        error?.response?.data?.message ||
          'Unable to contact sales right now. Please try again.'
      );
    } finally {
      setContactSalesLoading(false);
    }
  };

  return (
    <section className="pricing-container" id="pricing">
      <div className="page-container">
        <div className="pricing-panel">
          <div className="pricing-header">
            <h1>Upgrade your plan for advanced features</h1>
          </div>

          {loading ? (
            <div className="pricing-loader" aria-hidden="true">
              <div className="pricing-skel-card" />
              <div className="pricing-skel-card" />
              <div className="pricing-skel-card" />
            </div>
          ) : (
            <div className="pricing-cards">
              <article className="pricing-card pricing-card--free">
                <div className="pricing-card-head">
                  <span className="pricing-badge pricing-badge--free">
                    {freePlan?.name || 'Free'}
                  </span>
                  <p className="pricing-card-copy">
                    Getting started with Africa&apos;s energy market intelligence
                  </p>
                  <div className="pricing-card-price">
                    <h2>
                      {freePrice.symbol}
                      {freePrice.amountLabel}
                    </h2>
                    <p>per month</p>
                  </div>
                </div>
                <a
                  className="pricing-card-btn pricing-card-btn--free"
                  href="https://app.sonaroid.io/signup"
                  target="_blank"
                  rel="noreferrer"
                >
                  Get Started
                </a>
                <hr className="pricing-card-divider" />
                <div className="pricing-feature-list">
                  {FREE_FEATURES.map((feature, index) => (
                    <FeatureRow key={`free-${index}`} {...feature} />
                  ))}
                </div>
              </article>

              <article className="pricing-card pricing-card--premium">
                <div className="pricing-popular-badge">MOST POPULAR</div>
                <div className="pricing-card-head">
                  <span className="pricing-badge pricing-badge--premium">
                    {premiumPlan?.name || 'Premium'}
                  </span>
                  <p className="pricing-card-copy">
                    Deeper access to energy data and insights without disruption
                  </p>
                  <div className="pricing-card-price">
                    <h2>
                      {premiumPrice.symbol}
                      {premiumPrice.amountLabel}
                    </h2>
                    <p>per month</p>
                  </div>
                </div>
                <a
                  className="pricing-card-btn pricing-card-btn--primary"
                  href="https://app.sonaroid.io/signup"
                  target="_blank"
                  rel="noreferrer"
                >
                  Get Started
                </a>
                <hr className="pricing-card-divider" />
                <div className="pricing-feature-list">
                  {PREMIUM_FEATURES.map((feature, index) => (
                    <FeatureRow key={`premium-${index}`} {...feature} />
                  ))}
                </div>
              </article>

              <article className="pricing-card pricing-card--enterprise">
                <div className="pricing-card-head">
                  <span className="pricing-badge pricing-badge--enterprise">
                    Enterprise
                  </span>
                  <p className="pricing-card-copy pricing-card-copy--enterprise">
                    For companies wiring market intelligence into their operational
                    workflows.
                  </p>
                  <div className="pricing-card-price pricing-card-price--enterprise">
                    <h2>Bespoke</h2>
                    <p className="pricing-card-price-spacer" aria-hidden="true">
                      per month
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  className="pricing-card-btn pricing-card-btn--enterprise"
                  onClick={openContactModal}
                >
                  Contact Sales →
                </button>
                <hr className="pricing-card-divider pricing-card-divider--enterprise" />
                <div className="pricing-feature-list">
                  {ENTERPRISE_FEATURES.map((feature, index) => (
                    <FeatureRow key={`enterprise-${index}`} {...feature} dark />
                  ))}
                </div>
              </article>
            </div>
          )}

          {/* <p className="pricing-location-note">
            Your location is detected as {detectedLocation}, prices are shown in{' '}
            {displayCurrencyLabel} and billed in USD.
          </p> */}
        </div>
      </div>

      {isContactModalOpen ? (
        <div
          className="pricing-modal-overlay"
          role="presentation"
          onClick={closeContactModal}
        >
          <div
            className="pricing-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="pricing-contact-sales-title"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="pricing-modal-header">
              <h2 id="pricing-contact-sales-title">Contact Sales</h2>
              <button
                type="button"
                className="pricing-modal-close"
                onClick={closeContactModal}
                aria-label="Close contact sales form"
              >
                <IoClose size={20} />
              </button>
            </div>
            <p className="pricing-modal-copy">
              Tell us a bit about your team and we&apos;ll follow up with a
              bespoke Enterprise plan.
            </p>
            <form className="pricing-modal-form" onSubmit={handleContactSalesSubmit}>
              <label className="pricing-modal-field">
                <span>Name</span>
                <input
                  type="text"
                  name="name"
                  placeholder="Jane Doe"
                  value={contactForm.name}
                  onChange={handleContactFieldChange}
                  required
                  autoComplete="name"
                />
              </label>
              <label className="pricing-modal-field">
                <span>Email</span>
                <input
                  type="email"
                  name="email"
                  placeholder="user@example.com"
                  value={contactForm.email}
                  onChange={handleContactFieldChange}
                  required
                  autoComplete="email"
                />
              </label>
              <label className="pricing-modal-field">
                <span>
                  Company brief <em>(optional)</em>
                </span>
                <textarea
                  name="company_brief"
                  placeholder="Share a short note about your company or needs"
                  value={contactForm.company_brief}
                  onChange={handleContactFieldChange}
                  rows={4}
                />
              </label>
              {formError ? (
                <p className="pricing-modal-error" role="alert">
                  {formError}
                </p>
              ) : null}
              <button
                type="submit"
                className={`pricing-modal-submit${
                  contactSalesLoading ? ' is-loading' : ''
                }`}
                disabled={contactSalesLoading}
              >
                {contactSalesLoading ? 'Sending…' : 'Submit'}
              </button>
            </form>
          </div>
        </div>
      ) : null}

      {toast ? (
        <div
          className={`pricing-toast pricing-toast--${toast.type}`}
          role="status"
        >
          {toast.message}
        </div>
      ) : null}
    </section>
  );
};

export default Pricing;
